import { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { get } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { useAuth } from "@/context/AuthContext";
import { homeFor } from "@/components/RequireRole";
import { FullPageLoader } from "@/components/States";
import { FinalCta, Features, Pricing } from "@/components/home/HomeSections";
import { PublicFooter, PublicNav } from "@/components/home/PublicChrome";
import { Faq } from "@/components/home/Faq";
import { Button } from "@/components/ui/button";

const DESCRIPTION = "Warrant Whizz helps multi-role teams manage warrant runs and pre-warrant visits. Coordinate Locksmiths, Warrant Officers, Engineers and Dog Handlers, record customer needs, track status and photos, and export your work. 14-day free trial.";

const Hero = () => (
  <section className="relative overflow-hidden bg-slate-900" data-testid="home-hero">
    <div className="relative mx-auto max-w-6xl px-6 pb-24 pt-36 text-center sm:pt-44">
      <p className="home-rise font-data text-xs uppercase tracking-[0.25em] text-blue-400">For teams handling warrant work</p>
      <h1 className="home-rise font-display mx-auto mt-4 max-w-3xl text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl" style={{ animationDelay: "80ms" }}>Run every warrant job and pre-warrant visit from one place.</h1>
      <p className="home-rise mx-auto mt-6 max-w-xl text-base text-slate-300 sm:text-lg" style={{ animationDelay: "160ms" }}>Coordinate Locksmiths, Warrant Officers, Engineers and Dog Handlers across runs and pre-warrant visits, with customer needs, status and photos in one place.</p>
      <div className="home-rise mt-10 flex flex-wrap justify-center gap-3" style={{ animationDelay: "240ms" }}>
        <Button asChild size="lg" className="bg-blue-700 hover:bg-blue-800" data-testid="home-hero-signup"><Link to="/signup">Start your 14-day free trial <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
        <Button asChild size="lg" variant="outline" className="border-slate-600 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="home-hero-pricing"><a href="#pricing">See pricing</a></Button>
      </div>
    </div>
  </section>
);

export default function Home() {
  const { profile, loading } = useAuth();
  const { hash } = useLocation();
  const [plans, setPlans] = useState(null);
  useEffect(() => {
    get("/plans").then(setPlans).catch(() => setPlans([]));
  }, []);
  useEffect(() => {
    if (hash && plans) document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash, plans]);
  useSeo("Warrant Whizz | Warrant Work Management for Teams", DESCRIPTION, "/");
  if (loading) return <FullPageLoader />;
  if (profile) return <Navigate to={homeFor(profile)} replace />;
  return (
    <div className="min-h-screen bg-slate-900" data-testid="home-page">
      <PublicNav />
      <main>
        <Hero />
        <Features />
        <Pricing plans={plans} />
        <Faq />
        <FinalCta />
      </main>
      <PublicFooter />
    </div>
  );
}
